import { useQueryClient } from '@tanstack/react-query';
import { FC } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Box, Button, CheckCircleSolid, Flex, ModalBody, ModalCloseButton, ModalFooter, ModalHeader, rem, useToast } from '@bonusly/design-system';
import { EventFrequency, EventOccurrence } from '@bonusly/one-on-ones-entities';
import { CreateEventAPIData, useCreateEvent } from '@bonusly/one-on-ones-queries';
import { UserDetailEntity } from '@bonusly/user-entities';
import { useCurrentUser } from '@bonusly/who-am-i';

import { QueryKey as GrowthQueryKey } from '../../../../../modules/growth/types/growth-query-key';
import { QueryKey as TeamManagementQueryKey } from '../../../../../modules/team-management/types/team-management-types';
import { FormValue, QueryKey } from '../../types';
import { AnimatedBox } from '../animated-box';
import { EventDetails } from '../event-details/event-details';
import { EventForm } from '../event-form/event-form';
import { GuestAvatar } from '../user/guest-avatar';

function getCurrentTimeZone(): string {
	// get IANA timezone from browser (e.g. "America/Denver")
	return Intl.DateTimeFormat().resolvedOptions().timeZone;
}

export interface CreateMeetingContentProps {
	onClose: VoidFunction;
	guest: UserDetailEntity;
	openLinkCalendar: VoidFunction;
}

export const CreateMeetingContent: FC<CreateMeetingContentProps> = ({ guest, onClose, openLinkCalendar }) => {
	const user = useCurrentUser();
	const toast = useToast();
	const queryClient = useQueryClient();
	const { t } = useTranslation(['one_on_ones', 'common']);

	const today = new Date();
	// NOTE: Originally I had a hook `useEventForm(event?)` that was used in both the create and edit modals. But, in trying to
	// minimize conditionals in functions I decided to just add the `useForm` hook directly in the components and pass in the
	// exact data that was needed.
	const form = useForm<FormValue>({
		defaultValues: {
			dayOfWeek: today.getDay().toString(),
			occurrenceInMonth: EventOccurrence.First,
			frequency: EventFrequency.Weekly,
			startTime: null,
			endTime: null,
			meetingDate: today,
		},
	});

	// TOUR 8: Here's where the useCreateEvent hook comes into play. We're now using a centralized hook to make the call.
	// When it creates the record, it will automatically invalidate the necessary queries related to it! This is a huge win.
	const {
		data: event,
		isLoading,
		mutate,
	} = useCreateEvent({
		onSuccess: () => {
			// NOTE: These queries are not part of the global API at this time so we need
			// to manually invalidate them here. This will be updated though!
			queryClient.invalidateQueries([QueryKey.CurrentOneOnOne]);
			queryClient.invalidateQueries([QueryKey.Meetings]);
			queryClient.invalidateQueries([TeamManagementQueryKey.DirectReportsOverview]);
			queryClient.invalidateQueries({ queryKey: [GrowthQueryKey.UserEvents] });
			toast({ status: 'success', title: t('meeting.modal.form.success') });
		},
		onError: () => {
			onClose();
			toast({ status: 'error', title: t('meeting.modal.form.error') });
		},
	});

	/*
	TOUR 9: And here 👇 is how we get the data for our mutation. But where does this data come from? To be honest, the solution here did not
	*have* to be done this way. You could have used a simple `useState` to store the data and modify it. But, I wanted to show how our
	form library could be used in a more complex scenario. This is a good example of how you can use `react-hook-form` to manage a form
	with custom components.

	React Hook Form (https://react-hook-form.com/) works very well with basic forms just using things like `<input>` and `<select>` because
	it can perfrom a bunch of validation automatically. It can also connect to custom components, which we had to do for this. There are
	dropdown selectors and a day picker that are all custom.

	Ultimately the big differentiator between the Create an Edit forms is whether or not we have an `event` record to work with.
	Scroll up a bit and go into the `useEventForm()` hook definition file, then come back here. You'll see that it sets the default
	state of the form based on whether or not an `event` is passed in. But after that, the contents of the from are shared
	between the two components.👇 scroll down a bit to line 125.
	*/

	// NOTE: The formatting of the data to send to the API is handled in the create and edit modals because they need to make slightly different
	// calls. It's easier to see what's happening by doing it here instead of a single hook, which is where this was done before:
	// https://gitlab.com/bonusly/engineering/front-end/kaleidoscope/-/blob/e9b53010a0e2dd2a9ccf8f030790a6b278f0aa97/packages/common/meeting-modal/src/components/meeting/hooks/use-meeting-modal-form.ts
	const submit = (data: FormValue) => {
		// validate monthly occurrence
		if (data.frequency === EventFrequency.Monthly && !data.occurrenceInMonth) {
			form.setError('occurrenceInMonth', {
				type: 'custom',
				message: t('meeting.modal.form.occurrence_in_month_error'),
			});
			return;
		}

		// validate time fields
		if (!data.startTime) {
			form.setError('startTime', {
				type: 'custom',
				message: t('meeting.modal.form.start_time_error'),
			});
			return;
		}

		if (!data.endTime) {
			form.setError('endTime', {
				type: 'custom',
				message: t('meeting.modal.form.end_time_error'),
			});
			return;
		}

		// extract schedule components from time pickers
		const hours = data.startTime.getHours().toString().padStart(2, '0');
		const minutes = data.startTime.getMinutes().toString().padStart(2, '0');
		const seconds = data.startTime.getSeconds().toString().padStart(2, '0');

		// meeting date as YYYY-MM-DD
		const year = data.meetingDate.getFullYear();
		const month = (data.meetingDate.getMonth() + 1).toString().padStart(2, '0');
		const day = data.meetingDate.getDate().toString().padStart(2, '0');

		// build complete payload after validation
		const payload: CreateEventAPIData = {
			frequency: data.frequency,
			schedule_day_of_week: data.dayOfWeek,
			schedule_occurrence_in_month: data.frequency === EventFrequency.Monthly ? data.occurrenceInMonth : null,
			schedule_time_of_day: `${hours}:${minutes}:${seconds}`,
			schedule_duration: Math.floor((data.endTime.getTime() - data.startTime.getTime()) / 1000),
			schedule_time_zone: getCurrentTimeZone(),
			schedule_date: `${year}-${month}-${day}`,
			guest_id: guest.managerId === user.id ? guest.id : user.id,
			host_id: guest.managerId === user.id ? user.id : guest.id,
		};

		mutate(payload);
	};

	return (
		<AnimatedBox minH={440}>
			<Box as="form" onSubmit={form.handleSubmit(submit)} tabIndex={0}>
				<ModalHeader>
					{event ? (
						<Flex alignItems="center" gap={rem(10)}>
							<CheckCircleSolid color="points.fg" size="lg" />
							<Box>{t('meeting.modal.success_header_create')}</Box>
						</Flex>
					) : (
						t('meeting.modal.header_create')
					)}
				</ModalHeader>
				<ModalCloseButton size="lg" borderRadius="full" />

				<ModalBody px={5} pt={4} pb={0}>
					<Flex direction="column" gap={5}>
						<GuestAvatar guest={guest} event={event} />

						{event ? (
							<EventDetails event={event} onClose={onClose} />
						) : (
							// NOTE: The EventForm component wraps a couple custom components, but it doesn't care whether or not the data is default data, or
							// if it comes from an existing record. It just displays what's there. The determining factor is the `useEventForm` hook. This limits
							// the amount of conditional logic to just that one point.
							<FormProvider {...form}>
								<EventForm isNewMeeting={true} openLinkCalendar={openLinkCalendar} />
							</FormProvider>
						)}
					</Flex>
				</ModalBody>

				<ModalFooter p={5}>
					{/* NOTE: Note that there are still plenty of places where we have ternaries to show different components.
						I'm OK with this because they're small and focused. If we were going to show significantly different views I'd probably
						build out whole new components. */}
					{event ? (
						<Button colorScheme="secondary" variant="alpha" onClick={onClose}>
							{t('common:close')}
						</Button>
					) : (
						<>
							<Flex alignItems="center" gap={2} justifyContent="space-between" width="full">
								<Button colorScheme="secondary" onClick={openLinkCalendar} variant="alpha">
									Go back
								</Button>

								<Flex gap={{ base: 1, md: 2 }}>
									<Button color="primary.fg" isDisabled={isLoading} mr={3} onClick={onClose} variant="ghost">
										{t('common:cancel')}
									</Button>
									<Button colorScheme="primary" isActive={isLoading} type="submit">
										{t('one_on_ones:meeting.modal.form.submit_create')}
									</Button>
								</Flex>
							</Flex>
						</>
					)}
				</ModalFooter>
			</Box>
		</AnimatedBox>
	);
};

/*
TOUR 10: So now we can create an edit meetings, all with automatic control of the query cache, and we're guaranteed to always be working with an `EventEntity`
data instance. Let's check out one more thing and then we'll wrap up this tour. We want to make sure everything works, so let's see how tests are handled.
Let's go to packages/common/meeting-modal/src/components/create-meeting-modal/__tests__/create-meeting-modal.test.tsx
*/
